import { z } from 'zod';

// Media Schema
export const MediaSchema = z.object({
  id: z.string().cuid(),
  filename: z.string().min(1).max(255),
  path: z.string().min(1).max(500),
  url: z.string().url(),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().min(1),
  width: z.number().int().min(1).optional(),
  height: z.number().int().min(1).optional(),

  // Metadata
  altText: z.string().max(500).optional(),
  title: z.string().max(255).optional(),
  description: z.string().max(1000).optional(),
  tags: z.string().max(1000).optional(),
  folder: z.string().max(100).optional(),

  // Organization
  isPublic: z.boolean().default(true),
  uploadedById: z.number().int(),

  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Input schemas
export const CreateMediaInput = z.object({
  filename: z.string().min(1).max(255),
  path: z.string().min(1).max(500),
  url: z.string().url(),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().min(1),
  width: z.number().int().min(1).optional(),
  height: z.number().int().min(1).optional(),
  altText: z.string().max(500).optional(),
  title: z.string().max(255).optional(),
  description: z.string().max(1000).optional(),
  tags: z.string().max(1000).optional(),
  folder: z.string().max(100).optional(),
  isPublic: z.boolean().optional(),
});

export const UpdateMediaInput = z.object({
  filename: z.string().min(1).max(255).optional(),
  altText: z.string().max(500).optional(),
  title: z.string().max(255).optional(),
  description: z.string().max(1000).optional(),
  tags: z.string().max(1000).optional(),
  folder: z.string().max(100).optional(),
  isPublic: z.boolean().optional(),
});

// Query schemas
export const MediaQueryInput = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  mimeType: z.string().optional(),
  folder: z.string().optional(),
  tags: z.string().optional(),
  orderBy: z.enum(['filename', 'createdAt', 'size', 'title']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// Upload schemas
export const MediaUploadInput = z.object({
  altText: z.string().max(500).optional(),
  title: z.string().max(255).optional(),
  description: z.string().max(1000).optional(),
  tags: z.string().max(1000).optional(),
  folder: z.string().max(100).optional(),
  isPublic: z.boolean().default(true),
});

// Response schemas
export const MediaListResponse = z.object({
  data: z.array(MediaSchema),
  pagination: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    pages: z.number().int(),
  }),
});

// Type exports
export type Media = z.infer<typeof MediaSchema>;
export type CreateMediaInput = z.infer<typeof CreateMediaInput>;
export type UpdateMediaInput = z.infer<typeof UpdateMediaInput>;
export type MediaQueryInput = z.infer<typeof MediaQueryInput>;
export type MediaUploadInput = z.infer<typeof MediaUploadInput>;
export type MediaListResponse = z.infer<typeof MediaListResponse>;

// File type helpers
export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/svg+xml',
  'image/gif',
  'image/ico',
] as const;

export const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/avi',
  'video/quicktime',
  'video/x-msvideo',
] as const;

export const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
] as const;

export const ALLOWED_MIME_TYPES = [
  ...IMAGE_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES,
  ...VIDEO_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
] as const;

// File size limits (in bytes)
export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

// Helper functions
export const isImageType = (mimeType: string): boolean => {
  return IMAGE_MIME_TYPES.includes(mimeType as any);
};

export const isDocumentType = (mimeType: string): boolean => {
  return DOCUMENT_MIME_TYPES.includes(mimeType as any);
};

export const getFileTypeCategory = (mimeType: string): 'image' | 'document' | 'video' | 'audio' | 'other' => {
  if (IMAGE_MIME_TYPES.includes(mimeType as any)) return 'image';
  if (DOCUMENT_MIME_TYPES.includes(mimeType as any)) return 'document';
  if (VIDEO_MIME_TYPES.includes(mimeType as any)) return 'video';
  if (AUDIO_MIME_TYPES.includes(mimeType as any)) return 'audio';
  return 'other';
};
